import React, { useEffect, useMemo, useState } from "react";

const DEFAULT_API_BASE = process.env.REACT_APP_API_BASE || "https://mystockpicks.onrender.com";

function Pill({ children }) {
  return <span className="px-3 py-1 rounded-full text-xs bg-gray-800 border border-gray-700">{children}</span>;
}

function Toggle({ value, onChange, disabled }) {
  return (
    <button disabled={disabled} onClick={() => onChange(!value)}
      className={`px-3 py-2 rounded-lg border text-sm ${value ? "bg-green-900/30 border-green-700" : "bg-gray-800 border-gray-700"}`}>
      {value ? "ON" : "OFF"}
    </button>
  );
}

function Row({ label, children }) {
  return <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-800"><div className="text-sm text-gray-300">{label}</div><div>{children}</div></div>;
}

export default function CryptoSwingBotPanel({ apiBase = DEFAULT_API_BASE, token = "" }) {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }), [token]);

  async function call(path, options = {}) {
    const res = await fetch(`${apiBase}${path}`, { ...options, headers: { ...headers, ...(options.headers || {}) } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.ok === false) throw new Error(data?.error || `Request failed (${res.status})`);
    return data;
  }

  async function refresh() {
    setError("");
    try {
      const [s, st, lg] = await Promise.all([
        call("/api/crypto-swing-bot/settings"),
        call("/api/crypto-swing-bot/status"),
        call("/api/crypto-swing-bot/logs?limit=120"),
      ]);
      setSettings(s.settings || {});
      setStatus(st || {});
      setLogs(lg.logs || []);
      const top = st?.state?.last_top_setups_json;
      if (typeof top === "string") {
        try { setCandidates(JSON.parse(top) || []); } catch (_) {}
      }
    } catch (e) { setError(e.message || String(e)); }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [apiBase, token]);

  async function patch(payload) {
    setBusy(true); setError("");
    try {
      const out = await call("/api/crypto-swing-bot/settings", { method: "POST", body: JSON.stringify(payload) });
      setSettings(out.settings || {});
      await refresh();
    } catch (e) { setError(e.message || String(e)); } finally { setBusy(false); }
  }

  async function scan() {
    setBusy(true); setError("");
    try {
      const out = await call("/api/crypto-swing-bot/scan", { method: "POST", body: JSON.stringify({ limit: 10 }) });
      setCandidates(out.candidates || []);
      await refresh();
    } catch (e) { setError(e.message || String(e)); } finally { setBusy(false); }
  }

  async function runTick() {
    setBusy(true); setError("");
    try {
      const out = await call("/api/crypto-swing-bot/run-tick", { method: "POST", body: JSON.stringify({ execute: true, force_scan: true }) });
      setCandidates(out.candidates || []);
      await refresh();
    } catch (e) { setError(e.message || String(e)); } finally { setBusy(false); }
  }

  async function runExits() {
    setBusy(true); setError("");
    try {
      await call("/api/crypto-swing-bot/run-exits", { method: "POST", body: JSON.stringify({ execute: true }) });
      await refresh();
    } catch (e) { setError(e.message || String(e)); } finally { setBusy(false); }
  }

  if (!settings) return <div className="text-sm text-gray-400">Loading Crypto Swing Bot…</div>;
  const open = status?.open_positions || [];
  const recent = status?.recent_trades || [];
  let audit = {};
  try {
    const rawAudit = status?.state?.last_scan_audit_json;
    audit = typeof rawAudit === "string" ? (JSON.parse(rawAudit) || {}) : (rawAudit || {});
  } catch (_) { audit = {}; }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-purple-800 bg-purple-950/20 p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-lg font-semibold">Crypto Swing Bot</div>
            <div className="text-xs text-gray-300 mt-1 max-w-3xl">
              Multi-day long-spot engine. Daily + 4h define trend, 1h confirms momentum, and 30m times entries. It is independent from the faster Market Crypto Bot.
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Pill>{settings.engine_version || "v19.11.1.1"}</Pill>
            <Pill>{settings.shadow_mode ? "SHADOW" : "LIVE-CAPABLE"}</Pill>
            <Pill>{open.length}/{settings.max_positions || 2} open</Pill>
            {audit?.eligible ? <Pill>{audit.considered || 0}/{audit.eligible} universe</Pill> : null}
            {audit?.deep_scanned ? <Pill>{audit.deep_scanned} deep / scan</Pill> : null}
          </div>
        </div>
        {!settings.live_rollout_gate ? (
          <div className="mt-3 text-xs text-yellow-200">Live Crypto Swing entries are server-locked. Shadow mode can be tested without touching Robinhood positions.</div>
        ) : null}
      </div>

      {error ? <div className="rounded-lg border border-red-800 bg-red-950/30 p-3 text-sm text-red-300">{error}</div> : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
          <Row label="Crypto Swing Enabled"><Toggle disabled={busy} value={Boolean(settings.enabled)} onChange={(v) => patch({ enabled: v })} /></Row>
          <Row label="Shadow Mode"><Toggle disabled={busy} value={Boolean(settings.shadow_mode)} onChange={(v) => patch({ shadow_mode: v })} /></Row>
          <Row label="Scan All Robinhood Crypto"><Toggle disabled={busy} value={Boolean(settings.scan_all)} onChange={(v) => patch({ scan_all: v })} /></Row>
          <Row label="Minimum Swing Score"><input disabled={busy} type="number" min="70" max="99" value={settings.min_score ?? 86} onChange={(e) => patch({ min_score: Number(e.target.value) })} className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-right" /></Row>
          <Row label="Max Swing Positions"><input disabled={busy} type="number" min="1" max="3" value={settings.max_positions ?? 2} onChange={(e) => patch({ max_positions: Number(e.target.value) })} className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-right" /></Row>
          <Row label="Max Trade $"><input disabled={busy} type="number" min="2" step="1" value={settings.max_trade_usd ?? 15} onChange={(e) => patch({ max_trade_usd: Number(e.target.value) })} className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-right" /></Row>
          <Row label="Max Position %"><input disabled={busy} type="number" min="0.5" max="10" step="0.5" value={settings.max_position_pct ?? 4} onChange={(e) => patch({ max_position_pct: Number(e.target.value) })} className="w-24 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-right" /></Row>
        </div>
        <div className="bg-gray-950 border border-gray-800 rounded-xl p-4">
          <Row label="Profit Protection Arms"><span className="text-sm">+{Number(settings.profit_arm_pct ?? 5).toFixed(1)}%</span></Row>
          <Row label="Hard Stop"><span className="text-sm">{Number(settings.hard_stop_pct ?? -8).toFixed(1)}%</span></Row>
          <Row label="Emergency Stop"><span className="text-sm">{Number(settings.emergency_stop_pct ?? -12).toFixed(1)}%</span></Row>
          <Row label="No-Progress Time Stop"><span className="text-sm">{settings.no_progress_hours ?? 72}h</span></Row>
          <Row label="Maximum Hold"><span className="text-sm">{settings.max_holding_days ?? 10} days</span></Row>
          <Row label="Live Entries"><span className={`text-sm ${settings.allow_live ? "text-red-300" : "text-green-300"}`}>{settings.allow_live ? "ARMED" : "BLOCKED"}</span></Row>
          <Row label="Universe / Deep Coverage"><span className="text-sm">{audit?.considered ?? "-"}/{audit?.eligible ?? "-"} · {audit?.deep_scanned ?? "-"} deep</span></Row>
          <Row label="Rotation Coverage"><span className="text-sm">{audit?.estimated_full_coverage_minutes ? `~${audit.estimated_full_coverage_minutes} min` : "-"}</span></Row>
          <div className="text-[11px] text-gray-500 mt-3">Technical exits use completed Daily/4h/1h candles. Robinhood bid is used only for executable P/L and sell pricing.</div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button disabled={busy} onClick={scan} className="px-4 py-2 rounded-lg bg-blue-900/30 border border-blue-700 text-sm">Scan Now</button>
        <button disabled={busy} onClick={runTick} className="px-4 py-2 rounded-lg bg-purple-900/30 border border-purple-700 text-sm">Run 1 Shadow Tick</button>
        <button disabled={busy} onClick={runExits} className="px-4 py-2 rounded-lg bg-green-900/30 border border-green-700 text-sm">Run Swing Exit Check</button>
        <button disabled={busy} onClick={refresh} className="px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm">Refresh</button>
      </div>

      <div className="bg-gray-950 border border-gray-800 rounded-xl p-4 overflow-auto">
        <div className="text-sm font-semibold mb-2">Top Crypto Swing Setups</div>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-gray-400 border-b border-gray-800"><th className="py-2">Coin</th><th>Score</th><th>Setup</th><th>Daily</th><th>4h</th><th>1h</th><th>30m</th><th>Expected Move</th><th>Decision</th></tr></thead>
          <tbody>{(candidates || []).map((r, i) => <tr key={`${r.symbol}-${i}`} className="border-b border-gray-900"><td className="py-2 font-semibold">{r.symbol}</td><td>{Number(r.score || 0).toFixed(1)}</td><td>{r.setup_type || "-"}</td><td>{Number(r.daily_score || 0).toFixed(1)}</td><td>{Number(r.h4_score || 0).toFixed(1)}</td><td>{Number(r.h1_score || 0).toFixed(1)}</td><td>{Number(r.m30_score || 0).toFixed(1)}</td><td>{Number(r.expected_move_pct || 0).toFixed(2)}%</td><td className={r.qualified ? "text-green-300" : "text-gray-400"}>{r.qualified ? (r.score_path === "high_quality_alignment_exception" ? "QUALIFIED · ALIGNMENT" : "QUALIFIED") : (r.chase_reasons?.[0] || r.reason || "WAIT")}</td></tr>)}
          {!candidates?.length ? <tr><td colSpan={9} className="py-3 text-gray-500">No Swing scan yet.</td></tr> : null}</tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-gray-950 border border-gray-800 rounded-xl p-4 overflow-auto"><div className="text-sm font-semibold mb-2">Open Crypto Swings</div><pre className="text-xs text-gray-300 whitespace-pre-wrap">{JSON.stringify(open, null, 2)}</pre></div>
        <div className="bg-gray-950 border border-gray-800 rounded-xl p-4 overflow-auto"><div className="text-sm font-semibold mb-2">Recent Swing Trades</div><pre className="text-xs text-gray-300 whitespace-pre-wrap">{JSON.stringify(recent.slice(0, 12), null, 2)}</pre></div>
      </div>

      <div className="bg-gray-950 border border-gray-800 rounded-xl overflow-hidden">
        <div className="p-3 text-sm font-semibold border-b border-gray-800">Crypto Swing Logs</div>
        <div className="max-h-72 overflow-auto"><table className="w-full text-xs"><thead className="bg-gray-900"><tr><th className="p-2 text-left">Time</th><th className="p-2 text-left">Level</th><th className="p-2 text-left">Symbol</th><th className="p-2 text-left">Event</th></tr></thead><tbody>{(logs || []).map((l) => <tr key={l.id} className="border-t border-gray-900"><td className="p-2">{l.created_at}</td><td className="p-2">{l.level}</td><td className="p-2">{l.symbol || "-"}</td><td className="p-2 text-gray-400">{l.message}</td></tr>)}{!logs?.length ? <tr><td className="p-3 text-gray-500" colSpan={4}>No Crypto Swing logs yet.</td></tr> : null}</tbody></table></div>
      </div>
    </div>
  );
}
